export function addTextInHTML(html, texto) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const paragraphs = doc.querySelector('p');

  if (paragraphs) {
    paragraphs.innerHTML = `${texto} ${paragraphs.innerHTML}`;
  }

  return doc.body.innerHTML;
}

/**
 * Limpia el texto de etiquetas HTML y entidades HTML
 * @param {string} html - Texto con HTML a limpiar
 * @returns {string} Texto limpio
 */
export function cleanHTML(html) {
  if (!html) return '';

  // Primero convertimos el HTML a texto plano
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let text = doc.body.textContent || '';

  // Limpiamos espacios extras
  text = text.trim().replace(/\s+/g, ' ');

  return text;
}

// Etiquetas que puede producir el WYSIWYG del CMS. Es un listado cerrado a propósito: `cleanHTML`
// parsea la cadena entera como HTML, así que un "<" literal de un título ("x<y", "2 < 3") se
// tomaría por el inicio de una etiqueta y se perdería cuanto va detrás. Reconociendo solo
// estas etiquetas, cualquier otro "<" se escapa antes de parsear y sobrevive como texto.
// Límite conocido: una etiqueta fuera del listado (un tag exótico o un custom element) se
// mostraría literal en vez de eliminarse.
const KNOWN_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'figcaption',
  'figure',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'section',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

// Etiquetas que separan visualmente el texto: `textContent` concatena sin separador, así que un
// título con salto de línea o con dos bloques llegaría con las palabras pegadas ("L1<br>L2").
const BLOCK_TAGS = new Set([
  'blockquote',
  'br',
  'div',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
]);

// Solo la forma de una etiqueta: el nombre se coteja al recorrer. Los valores entre comillas se
// saltan enteros, porque un '<' dentro de un atributo es válido y no cierra la etiqueta (el editor
// de fórmulas guarda el latex en data-original-math, así que una desigualdad llega como
// <span data-original-math="\(x<0\)">).
const HTML_TAG = /<(\/)?([a-z][a-z0-9]*)\b((?:"[^"]*"|'[^']*'|[^<>"'])*)>/gi;

// El cierre es obligatorio: sin él, un "<!--" suelto de un título se comería el resto del texto.
const HTML_COMMENT = /<!--.*?-->/gs;

const isKnownTag = (name) => KNOWN_TAGS.has(name);

// El primero de los dos que aparezca a partir de `from`. Son dos expresiones y no una alternancia
// para que ninguna crezca de complejidad.
function nextToken(value, from) {
  HTML_TAG.lastIndex = from;
  HTML_COMMENT.lastIndex = from;

  const tag = HTML_TAG.exec(value);
  const comment = HTML_COMMENT.exec(value);

  if (!tag) return comment;
  if (!comment) return tag;

  return comment.index < tag.index ? comment : tag;
}

function normalizeMarkup(value, { isTag = isKnownTag, keepComments = false } = {}) {
  let result = '';
  let cursor = 0;
  let token;

  while ((token = nextToken(value, cursor)) !== null) {
    // Un token con forma de etiqueta pero nombre desconocido se trata como texto: su '<' se
    // escapa igual que cualquier otro suelto (el token no puede contener otro '<', así que
    // escaparlo aquí es idéntico a no haberlo reconocido).
    const name = token[2]?.toLowerCase();
    const keepAsMarkup = name ? isTag(name, token[3], !!token[1]) : keepComments;
    const text = keepAsMarkup ? token[0] : token[0].replaceAll('<', '&lt;');
    const separator = keepAsMarkup && BLOCK_TAGS.has(name) ? ' ' : '';
    result += value.slice(cursor, token.index).replaceAll('<', '&lt;') + text + separator;
    cursor = token.index + token[0].length;
  }

  return result + value.slice(cursor).replaceAll('<', '&lt;');
}

/**
 * Texto plano de un valor editado con WYSIWYG ("<p>Unidad 1</p>"), para pintarlo como texto o
 * usarlo en nombres accesibles y breadcrumbs. Endurece `cleanHTML` en los dos puntos donde este
 * pierde información: los "<" literales y los bloques sin separador.
 * @param {string} value - Valor con posible markup
 * @returns {string} Texto plano
 */
export function htmlToPlainText(value) {
  if (typeof value !== 'string' || !value) return '';

  return cleanHTML(normalizeMarkup(value));
}

// Etiquetas que `normalizeMarkup` no reconoce y que no deben acabar pintadas literalmente: se
// añaden al listado al sanear para que se reconozcan como etiquetas y `sanitizeNode` las descarte
// con su contenido, en vez de escapar su '<' y mostrarlas como texto.
const UNSAFE_TAGS = new Set(['script', 'style', 'svg', 'math', 'template', 'noscript', 'iframe', 'object', 'embed']);

const RECOGNIZED_TAGS = new Set([...KNOWN_TAGS, ...UNSAFE_TAGS]);

// Rasgos que el '<' literal de un título no tiene: namespace (un pegado desde Word deja
// `<o:p></o:p>`), guion (un custom element) o atributos. Con cualquiera de ellos —o con la barra
// de cierre— el token es marcado ajeno, y al pintar como HTML vale más descartarlo que escaparlo y
// dejarlo a la vista. Un '<' que solo lleva un nombre detrás ("x<y>") sigue siendo texto.
const FOREIGN_MARKUP = /^[:\-\s/]/;

const isRenderableTag = (name, rest, closing) => RECOGNIZED_TAGS.has(name) || closing || FOREIGN_MARKUP.test(rest);

// Etiquetas que se conservan al pintar un valor del WYSIWYG como HTML. Es una whitelist: el resto
// (incluidos `a`, `img` y cualquier bloque) se sustituye por su texto, así que un título no puede
// inyectar enlaces, imágenes ni scripts.
const INLINE_TAGS = new Set(['b', 'em', 'i', 'mark', 's', 'small', 'span', 'strong', 'sub', 'sup', 'u']);

// `class` es imprescindible: el marcado de fórmulas del editor viaja en `fr-math*`, que es lo que
// reconoce el motor de LaTeX. Los `data-*` los usa el propio plugin. Nada más pasa, y en
// particular ningún `on*` ni `style`.
// El nombre se acota además a lo que acepta `setAttribute`: el parser admite comillas y acentos
// graves dentro de un nombre de atributo, y con ellos `setAttribute` lanza InvalidCharacterError.
const DATA_ATTRIBUTE = /^data-[a-z0-9_.-]+$/;

function isAllowedAttribute(name) {
  return name === 'class' || DATA_ATTRIBUTE.test(name);
}

function sanitizeNode(node, doc) {
  const fragment = doc.createDocumentFragment();

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      fragment.appendChild(doc.createTextNode(child.nodeValue));
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE) return;

    const name = child.tagName.toLowerCase();

    // El contenido de estas no es texto de autor, sino fuente que se pintaría a la vista.
    if (UNSAFE_TAGS.has(name)) return;

    const children = sanitizeNode(child, doc);

    if (!INLINE_TAGS.has(name)) {
      fragment.appendChild(children);
      return;
    }

    const clean = doc.createElement(name);
    Array.from(child.attributes).forEach((attribute) => {
      if (isAllowedAttribute(attribute.name)) clean.setAttribute(attribute.name, attribute.value);
    });
    clean.appendChild(children);
    fragment.appendChild(clean);
  });

  return fragment;
}

/**
 * HTML seguro de un valor editado con WYSIWYG, para pintarlo conservando el formato inline (las
 * cursivas y las fórmulas matemáticas que el editor permite en los títulos). Endurece el mismo
 * caso que `htmlToPlainText` con los "<" literales, y se queda solo con las etiquetas de
 * INLINE_TAGS y los atributos permitidos: del resto conserva el texto y tira la etiqueta.
 * Descarta también el marcado ajeno que no reconoce por nombre (`<o:p>` de un pegado desde Word,
 * un custom element) y los comentarios, para que no se pinten literales. TAN-9967 sustituye este
 * criterio por el del propio parser: al converger las dos ramas, este saneador debe pasar a ser el
 * perfil inline de aquel y no una segunda implementación.
 * @param {string} value - Valor con posible markup
 * @returns {string} HTML saneado
 */
export function sanitizeInlineHTML(value) {
  if (typeof value !== 'string' || !value) return '';

  const markup = normalizeMarkup(value, { isTag: isRenderableTag, keepComments: true });
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const container = doc.createElement('div');
  container.appendChild(sanitizeNode(doc.body, doc));

  return container.innerHTML.trim();
}

/**
 * Convierte una cadena CSS con variables (formato :root) en un objeto JavaScript
 * Las claves del objeto resultante están en camelCase y se eliminan los prefijos '--'
 * Los valores de URL se limpian eliminando 'url(' y ')'
 *
 * @param {string} cssString - String CSS con variables en formato :root {...}
 * @returns {Object} Objeto con las variables CSS como propiedades
 *
 * @example
 * // Input: ":root{--color-primary:#ff0000;--bg-image:url('img.jpg');}"
 * // Output: {colorPrimary: "#ff0000", bgImage: "img.jpg"}
 */

export function parseCSSVarsToObject(cssString) {
  // Remove :root {} and split by semicolon
  const cleanString = cssString?.replace(/:root\s*{/, '')?.replace(/}$/, '');
  const varsArray = cleanString?.split(';').filter(Boolean);

  return (
    varsArray?.reduce((acc, variable) => {
      // Find the position of the first colon
      const colonIndex = variable?.indexOf(':');
      if (colonIndex === -1) return acc;

      // Split into key and value using the first colon only
      const key = variable?.substring(0, colonIndex)?.trim();
      const value = variable?.substring(colonIndex + 1)?.trim();

      if (key && value) {
        // Remove '--' from key and convert to camelCase
        const propertyName = key?.replace('--', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());

        // Validate URL values
        if (value.startsWith('url(')) {
          const urlValue = value.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '');
          try {
            new URL(urlValue);
            acc[propertyName] = urlValue;
          } catch {
            acc[propertyName] = undefined;
          }
        } else {
          acc[propertyName] = value;
        }
      }
      return acc;
    }, {}) || {}
  );
}
